import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../logo/swarovski-1.png';

// ── helpers ────────────────────────────────────────────────────────────────
const NumberToWords = (num) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens  = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convert = (n) => {
        if (n < 10)       return units[n];
        if (n < 20)       return n === 10 ? 'Ten' : teens[n - 11];
        if (n < 100)      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
        if (n < 1000)     return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
        if (n < 100000)   return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
        if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
        return 'Number is too large';
    };
    if (num === 0) return 'Zero';
    return convert(num);
};

const amountInWords = (amount) => {
    const formatted = parseFloat(amount).toFixed(2);
    const [rupees, paise] = formatted.split('.');
    let words = `Rupees ${NumberToWords(parseInt(rupees))} Only`;
    if (paise !== '00') words += ` and ${NumberToWords(parseInt(paise))} Paise Only`;
    return words;
};

const fmt2    = (v) => (parseFloat(v) || 0).toFixed(2);
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
const nz      = (v) => (parseFloat(v) || 0) !== 0;

// ── main export ────────────────────────────────────────────────────────────
const generateSaleReturnPDF = (labelData) => {
    if (!labelData) return;

    // Normalise API response shape
    const data = Array.isArray(labelData) ? labelData[0] : labelData;
    const sp   = data?.SalePrint   ?? data;
    const det  = data?.SalePrintDetails ?? [];

    if (!sp || !sp.CompanyName) {
        console.error('generateSaleReturnPDF: unexpected data shape', labelData);
        return;
    }

    // ── Document setup ────────────────────────────────────────────────────
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const PW  = doc.internal.pageSize.getWidth();   // 210 mm
    const PH  = doc.internal.pageSize.getHeight();  // 297 mm
    const ML  = 5;
    const MR  = 5;
    const CW  = PW - ML - MR;                       // 200 mm

    let y = 6;

    // ── Logo ──────────────────────────────────────────────────────────────
    try {
        const imgW = 22, imgH = 22;
        doc.addImage(logo, 'PNG', (PW - imgW) / 2, y, imgW, imgH);
        y += imgH + 2;
    } catch (_) { y += 4; }

    // ── Company header (centred, bold) ────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Dealer : SWAROVSKI', PW / 2, y, { align: 'center' }); y += 5;

    doc.setFontSize(11);
    doc.text(sp.CompanyName || '', PW / 2, y, { align: 'center' }); y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    if (sp.AddressLine) {
        const addrLines = doc.splitTextToSize(sp.AddressLine, CW - 10);
        addrLines.forEach(l => { doc.text(l, PW / 2, y, { align: 'center' }); y += 4; });
    }
    doc.text(`Phone No:${sp.ContactNumber1 || ''}`, PW / 2, y, { align: 'center' }); y += 4;
    doc.text(`Email:${sp.Email || ''}`, PW / 2, y, { align: 'center' }); y += 5;

    // ── Meta row: State | CIN / GST | Invoice / Date / Time ──────────────
    const c1x = ML;
    const c2x = ML + CW * 0.38;
    const c3x = ML + CW * 0.72;

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');

    doc.text(`State Code :${sp.BranchStateCode || ''}`,  c1x, y);
    doc.text(`Invoice Number :${sp.BillPrefix}/SR/${sp.BillNumber}/${sp.FinYear}`, c3x, y); y += 4.5;

    doc.text(`State Name :${sp.BranchStateName || ''}`,  c1x, y);
    doc.text(`CIN Number :${sp.CINNumber || ''}`,         c2x, y - 2.25);
    doc.text(`GST Number :${sp.CompanyGSTNumber || ''}`,  c2x, y + 2.25);
    doc.text(`Date :${fmtDate(sp.BillDate)}`,             c3x, y); y += 4.5;

    doc.text(`Time :${fmtTime(sp.CreatedDate)}`,          c3x, y); y += 5;

    // ── SALES RETURN title + extra meta row ──────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('SALES RETURN', PW / 2, y, { align: 'center' }); y += 5;

    // Extra meta row: Sale Bill No (left) | SALES RETURN (centre) | Sale Bill Date (right)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(`Sale Bill No: ${sp.SaleBillNumber || ''}`, c1x, y);
    // doc.text('SALES RETURN', PW / 2, y, { align: 'center' });
    doc.text(`Sale Bill Date: ${sp.SaleBillDate ? fmtDate(sp.SaleBillDate) : ''}`, PW - MR, y, { align: 'right' });
    y += 6;

    // ── Customer box ──────────────────────────────────────────────────────
    doc.setDrawColor(0); doc.setLineWidth(0.3);
    const custLines = [];
    custLines.push({ text: 'Customer Name & Address', bold: true });
    custLines.push({ text: sp.CustomerName || '',     bold: false });
    custLines.push({ text: sp.ContactNumber || '',    bold: false });
    if (sp.CustomerAddress) custLines.push({ text: sp.CustomerAddress, bold: false });
    if (sp.GSTNumber)       custLines.push({ text: `GST Number: ${sp.GSTNumber}`, bold: false });

    const custBoxH = Math.max(18, custLines.length * 5 + 4);
    doc.rect(ML, y, CW, custBoxH);
    let cy = y + 5;
    custLines.forEach(cl => {
        doc.setFont('helvetica', cl.bold ? 'bold' : 'normal');
        doc.setFontSize(9);
        doc.text(cl.text, ML + 2, cy);
        cy += 4.5;
    });
    y += custBoxH;

    // ── Items table ───────────────────────────────────────────────────────
    // Columns: CODE, DESCRIPTION, HSN, MRP, DISC%, QTY, RATE, GST%, VALUE
    const tableBody = det.map(r => [
        r.ItemCode  || '',
        r.ItemName  || '',
        r.HSNCode   || '',
        nz(r.Rate)                 ? fmt2(r.Rate)                 : '',
        nz(r.DiscountPercentage)   ? fmt2(r.DiscountPercentage)   : '',
        nz(r.Quantity)             ? String(r.Quantity)           : '',
        nz(r.TaxableAmount)        ? fmt2(r.TaxableAmount)        : '',
        nz(r.GSTRate)              ? fmt2(r.GSTRate)              : '',
        nz(r.TotalAmount)          ? fmt2(r.TotalAmount)          : '',
    ]);

    autoTable(doc, {
        startY: y,
        head: [['CODE', 'DESCRIPTION', 'HSN', 'MRP', 'DISC %', 'QTY', 'RATE', 'GST %', 'VALUE']],
        body: tableBody,
        theme: 'grid',
        styles: {
            fontSize: 7.5,
            cellPadding: { top: 1.5, bottom: 1.5, left: 1.5, right: 1.5 },
            font: 'helvetica',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.25,
            minCellHeight: 6,
        },
        headStyles: {
            fillColor:  [255, 255, 255],
            textColor:  [0, 0, 0],
            fontStyle:  'bold',
            fontSize:   7.5,
            halign:     'center',
            lineColor:  [0, 0, 0],
            lineWidth:  0.3,
        },
        columnStyles: {
            0: { cellWidth: 16, halign: 'left'   },   // CODE
            1: { cellWidth: 58, halign: 'left'   },   // DESCRIPTION
            2: { cellWidth: 18, halign: 'center' },   // HSN
            3: { cellWidth: 16, halign: 'right'  },   // MRP
            4: { cellWidth: 14, halign: 'right'  },   // DISC%
            5: { cellWidth: 12, halign: 'right'  },   // QTY
            6: { cellWidth: 22, halign: 'right'  },   // RATE
            7: { cellWidth: 14, halign: 'right'  },   // GST%
            8: { cellWidth: 30, halign: 'right'  },   // VALUE
        },
        margin: { left: ML, right: MR },
    });

    y = doc.lastAutoTable.finalY;

    // ── GST row (left box + total label + total value) ────────────────────
    const isIGST   = parseFloat(sp.IGSTAmount) > 0;
    const gstLine1 = isIGST
        ? `IGST Amount: ${fmt2(sp.IGSTAmount)}`
        : `CGST Amount: ${fmt2(sp.CGSTAmount)}`;
    const gstLine2 = isIGST ? null : `SGST Amount: ${fmt2(sp.SGSTAmount)}`;

    const gstBoxH = gstLine2 ? 14 : 11;
    const gstColW = CW * 0.50;
    const rightW  = CW - gstColW;
    const midY    = y + gstBoxH / 2 + 3;

    doc.setLineWidth(0.3);
    doc.setDrawColor(0);

    doc.line(ML,            y,           ML,            y + gstBoxH);
    doc.line(ML,            y,           ML + gstColW,  y);
    doc.line(ML,            y + gstBoxH, ML + gstColW,  y + gstBoxH);

    doc.rect(ML + gstColW, y, rightW, gstBoxH);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(gstLine1, ML + 2, y + 4);
    if (gstLine2) doc.text(gstLine2, ML + 2, y + 9);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('TOTAL AMOUNT (Inclusive of GST)', ML + gstColW + 2, midY);
    doc.setFontSize(10);
    doc.text(fmt2(sp.GrandTotal), ML + gstColW + rightW - 2, midY, { align: 'right' });

    y += gstBoxH;

    // ── Amount in words + Authorised Signatory ────────────────────────────
    const wordsColW = CW * 0.65;
    const sigColW   = CW - wordsColW;
    const words     = amountInWords(sp.GrandTotal);
    const wordLines = doc.splitTextToSize(words, wordsColW - 4);
    const wordsBoxH = Math.max(12, wordLines.length * 4 + 5);

    doc.setLineWidth(0.3);
    doc.line(ML,              y,              ML,              y + wordsBoxH);
    doc.line(ML,              y,              ML + wordsColW,  y);
    doc.line(ML,              y + wordsBoxH,  ML + wordsColW,  y + wordsBoxH);

    doc.rect(ML + wordsColW, y, sigColW, wordsBoxH);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(wordLines, ML + 2, y + 4);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Authorised Signatory',
        ML + wordsColW + sigColW / 2, y + wordsBoxH - 3, { align: 'center' });   
    

    // ── Open print dialog ─────────────────────────────────────────────────
    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const win = window.open(blobUrl, '_blank');
    if (win) {
        win.addEventListener('load', () => { win.focus(); win.print(); });
    }
};

export default generateSaleReturnPDF;
